import SolrCore from './SolrCore';
import type SolrFq from './SolrFq';
import type SolrSort from './SorlSort';

// make sure to sync up commonFl with StandardDoc
const commonFl = ['id', '_type_s', 'payload_s'] as const;
type StandardDoc = {
  id: string;
  _type_s: string;
  payload_s: string;
};

type SolrQueryResponse = {
  response: {
    numFound: number;
    numFoundExact: boolean;
    start: number;
    docs: Array<StandardDoc>;
  };
};

export default class SolrQuery {
  private readonly core: SolrCore;

  /** fq (filter query) */
  private fq?: SolrFq;

  /** defines the sort order */
  private sort?: SolrSort;

  private start: number;

  private rows: number;

  private constructor({ core }: { core: SolrCore }) {
    this.core = core;
    this.start = 0;
    this.rows = 50;
  }

  /** return a new SolrQuery with default values */
  public static devObj(): SolrQuery {
    return new SolrQuery({
      core: SolrCore.devObj,
    });
  }

  public static resourceCenter(): SolrQuery {
    return new SolrQuery({
      core: SolrCore.resourceCenter,
    });
  }

  /** Set the fq (filter query) for the Solr query */
  public setFq(fq: SolrFq): SolrQuery {
    this.fq = fq;
    return this;
  }

  /** Set the sort order for the Solr query */
  public setSort(sort: SolrSort): SolrQuery {
    this.sort = sort;
    return this;
  }

  public setRange({ start, rows }: { start: number; rows: number }): SolrQuery {
    this.start = start;
    this.rows = rows;
    return this;
  }

  /** Should only be used in useSolrQuery */
  public async query<T>() {
    if (import.meta.server) {
      return {
        docs: [],
        numFound: 0,
        numFoundExact: true,
        start: 0,
      };
    }

    const org = document.location.origin;
    const qUrl = new URL(`${org}/api/s/${this.core.asString()}/select`);

    qUrl.searchParams.set('wt', 'json');
    qUrl.searchParams.set('q', '*:*');
    qUrl.searchParams.set('fl', commonFl.join(','));
    qUrl.searchParams.set('start', this.start.toString());
    qUrl.searchParams.set('rows', this.rows.toString());
    if (this.fq) {
      this.fq.setUrlParams(qUrl);
    }
    if (this.sort) {
      this.sort.setUrlParams(qUrl);
    }

    const tic = Date.now();
    const result = await $fetch<SolrQueryResponse>(qUrl.href, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'include',
    })
      .catch((error) => {
        console.error('Error fetching Solr query:', error);
        return {
          response: {
            numFound: 0,
            numFoundExact: true,
            start: 0,
            docs: [],
          },
        };
      })
      .finally(() => {
        const toc = Date.now();
        console.log(`Solr query took ${toc - tic}ms`, qUrl.href);
      });

    const { response } = result;
    if (!response) {
      throw new Error('No response in Solr query');
    }
    const { docs, numFound, numFoundExact, start } = response;

    const parsedDocs = docs.map((doc) => JSON.parse(doc.payload_s) as T);

    return { docs: parsedDocs, numFound, numFoundExact, start };
  }
}
